import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { BellRing, X } from "lucide-react";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { Button } from "../../components/ui/button";
import { useDismissNotification, useInAppNotifications } from "./api";
import { notificationTarget } from "./target";

/**
 * In-app reminder banner (F-014/AC-015, KOMP-107). Mounted once in the
 * authenticated shell above the routed content, so pending reminders greet the
 * user on every screen after login. Not color-alone: amber block PLUS bell
 * icon PLUS "Erinnerung(en)" heading. No loading skeleton - the banner simply
 * appears when data arrives; a failed fetch stays silent (console only) so a
 * broken reminder feed never disturbs the app. Dismiss errors DO toast (the
 * user acted), except a 409 "already dismissed", which the api layer treats
 * as success.
 */
export function ReminderBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const query = useInAppNotifications();
  const dismiss = useDismissNotification();

  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };

  useEffect(() => {
    if (query.error) {
      console.error("In-app notifications fetch failed:", query.error);
    }
  }, [query.error]);

  const items = query.data ?? [];
  if (items.length === 0) return null;

  return (
    <section
      role="status"
      className="mb-6 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 text-amber-900 dark:text-amber-100"
    >
      <div className="flex items-center gap-2 font-semibold">
        <BellRing className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {items.length === 1
            ? t("notifications.headingOne")
            : t("notifications.headingMany", { count: items.length })}
        </span>
      </div>
      <ul className="mt-3 space-y-3">
        {items.map((notification) => {
          const target = notificationTarget(notification);
          return (
            <li
              key={notification.id}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{notification.title}</div>
                <p className="mt-0.5 text-sm">{notification.body}</p>
                {target ? (
                  <Link
                    to={target.to}
                    params={target.params}
                    className="mt-1 inline-block text-sm font-medium underline underline-offset-2 hover:text-amber-700"
                  >
                    {t("notifications.openIncident")}
                  </Link>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-amber-900 dark:text-amber-100 hover:bg-amber-100 hover:text-amber-950"
                aria-label={t("notifications.dismiss")}
                disabled={dismiss.isPending}
                onClick={() =>
                  dismiss.mutate(notification.id, {
                    onError: (error) => notifyApiError(error, errorMessages),
                  })
                }
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
